import {
  useGetRegions,
  usePermissions,
  useUpsertRegion,
} from '@dispatch/shared/hooks';
import { RegionsDetails, validateRegion } from '@dispatch/web/features/regions';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  useNotification,
  useNotificationMutationFactory,
} from '@dispatch/web/ui';
import { Region } from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';

const RegionDetailsPage = () => {
  const params = useParams();
  const { permissions } = usePermissions();
  const regionId: string = params.regionId || '';
  const { tenantId } = useCurrentUser();
  const { data: allRegions, status } = useGetRegions({
    tenantId: tenantId || '',
  });
  const [data, setData] = React.useState<Region | undefined>();
  const { notifyError } = useNotification();

  useEffect(() => {
    const getRegion: Region[] | undefined = allRegions?.data?.filter(
      (region) => region.id === regionId
    );
    if (getRegion?.length) {
      setData(getRegion[0]);
    }
  }, [regionId, setData, allRegions]);

  const { notifySuccess } = useNotificationMutationFactory('Region', 'update');
  const onSuccess = () => {
    notifySuccess();
  };

  const { upsertRegion, status: mutateStatus } = useUpsertRegion(
    tenantId || '',
    regionId,
    onSuccess
  );

  const handleSave = (value: Partial<Region>) => {
    // update config api with new config values
    const updateRegions = allRegions?.data?.map((region: Region) => {
      if (region.id === value.id) {
        value.editedAt = new Date().toISOString();
        return value;
      }
      return region;
    });

    const hasValidationErrors = validateRegion(
      value,
      allRegions?.data || [],
      notifyError
    );

    if (!hasValidationErrors) {
      upsertRegion(updateRegions as Region[]);

      // filter for the currently viewed region to display
      const getRegion: Partial<Region>[] | undefined = updateRegions?.filter(
        (region) => region.id === regionId
      );
      if (getRegion?.length) {
        setData(getRegion[0] as Region);
      }
    }
  };

  const handleDeactivate = (value: Region) => {
    if (!value) return;

    allRegions?.data?.map((region: Region) => {
      if (region.id === value.id) {
        region.isActive = false;
        region.editedAt = new Date().toISOString();
        region.deactivatedAt = new Date().toISOString();
      }
      return region;
    });

    if (allRegions && allRegions?.data?.length > 0) {
      upsertRegion(allRegions?.data);
    }
  };

  const handleActivate = (value: Region) => {
    if (!value) return;

    allRegions?.data?.map((region: Region) => {
      if (region.id === value.id) {
        region.isActive = true;
        region.editedAt = new Date().toISOString();
        region.deactivatedAt = null;
      }
      return region;
    });

    if (allRegions && allRegions?.data?.length > 0) {
      upsertRegion(allRegions?.data);
    }
  };

  return (
    <RegionsDetails
      onSave={handleSave}
      onDeactivate={handleDeactivate}
      onActivate={handleActivate}
      status={mutateStatus === 'loading' ? mutateStatus : status}
      data={data as Region}
      allRegions={allRegions?.data || []}
      permissions={permissions}
    />
  );
};

export default RegionDetailsPage;
